/**
 * The admin chrome: sidebar, top bar, scope switcher.
 *
 * **The navigation is server-driven.** It renders what `/auth/me` returned,
 * which is already the intersection of (the service catalog ∩ this
 * installation's enrolled services ∩ this user's permissions). The only thing
 * the client contributes is the `module id → {icon, route}` adapter it is
 * handed, because an icon and a route are genuinely platform-specific and a
 * label, a group and a gating permission are not.
 *
 * A module the adapter has no route for is **skipped**, which lets an app omit a
 * screen it has not built yet without breaking the shared catalog.
 */

import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  Menu,
  Moon,
  Sun,
  User,
  X,
} from 'lucide-react';
import { ProductAttribution } from '../branding/product-attribution';

import { appConfig } from '../../config/app-config';
import { cn } from '../../lib/cn';
import { usePersistentState } from '../../lib/hooks';
import { useNavigation, useSession, type NavItem, type ScopeOption } from '../../auth/session';
import { useTheme } from '../../theme/theme';
import { Avatar, Badge } from '../ui/surfaces';
import { DropdownMenu } from '../ui/overlays';
import { BrandMark } from './brand-mark';
import { ScrollArea } from '../ui/scroll-area';

/** What an app supplies for each module it has built. */
export interface NavRoute {
  to: string;
  icon: ReactNode;
}

/** `module id → route + icon`. The client's only contribution to navigation. */
export type NavAdapter = Record<string, NavRoute>;

export interface AppShellProps {
  adapter: NavAdapter;
  /** Live counts for the badges a module declared. */
  badges?: Record<string, number>;
  children: ReactNode;
}

export function AppShell({ adapter, badges, children }: AppShellProps) {
  const groups = useNavigation();
  const user = useSession((state) => state.user);
  const signOut = useSession((state) => state.signOut);
  const switchScope = useSession((state) => state.switchScope);
  const [collapsed, setCollapsed] = usePersistentState('connect.sidebar.collapsed', false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { mode, setMode } = useTheme();
  const location = useLocation();

  const scopes = user?.scopes ?? [];

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      {/* On a phone the sidebar is an overlay: the scrim and the sidebar share the
          overlay layer, the scrim first so the sidebar paints over it — and the
          top bar, on no layer, sits under both. */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-overlay bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      ) : null}
      {/* ── sidebar ─────────────────────────────────────── */}
      <aside
        className={cn(
          'flex shrink-0 flex-col border-r border-line bg-sidebar transition-[width] duration-200',
          collapsed ? 'w-17' : 'w-64',
          // On a phone it becomes an overlay rather than squeezing the content
          // into a column nothing fits in.
          'max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-overlay',
          mobileOpen ? 'max-md:w-64' : 'max-md:hidden',
        )}
      >
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-line px-3">
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <BrandMark />
            {!collapsed ? (
              <span className="truncate text-sm font-semibold text-heading">
                {appConfig().appName}
              </span>
            ) : null}
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="ml-auto rounded p-1 text-faint hover:text-body md:hidden"
            aria-label="Close navigation"
          >
            <X className="size-4" />
          </button>
        </div>

        <ScrollArea className="min-h-0 flex-1">
        <nav className="px-2 py-3" aria-label="Main">
          {groups.map((group) => {
            const items = group.items.filter((item) => adapter[item.id]);
            // A group whose every module is unrouted or withheld renders
            // nothing — a heading with no items under it is worse than no
            // heading.
            if (items.length === 0) return null;

            return (
              // Keyed by what the group holds, not its heading: two runs of
              // modules with no heading would otherwise share one key.
              <div key={items[0]!.id} className="mb-3">
                {group.group && !collapsed ? (
                  <p className="mb-1 px-2 text-2xs font-semibold uppercase tracking-wide text-faint">
                    {group.group}
                  </p>
                ) : null}
                <ul className="space-y-0.5">
                  {items.map((item) => (
                    <NavLink
                      key={item.id}
                      item={item}
                      route={adapter[item.id]!}
                      collapsed={collapsed}
                      badge={item.badge ? badges?.[item.badge] : undefined}
                      currentPath={location.pathname}
                      onNavigate={() => setMobileOpen(false)}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
        </ScrollArea>

        {/* The foot of the rail. Absent when the rail is collapsed to icons: the
            mark is a sentence, and a sentence squeezed into an icon rail is a smudge. */}
        {!collapsed ? (
          <div className="flex justify-center border-t border-line px-3 py-3">
            <ProductAttribution size="small" />
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="hidden h-10 shrink-0 items-center justify-center border-t border-line text-faint transition-colors hover:bg-surface-2 hover:text-body md:flex"
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
        </button>
      </aside>

      {/* ── main ────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-3">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="rounded p-1.5 text-muted hover:bg-surface-2 md:hidden"
            aria-label="Open navigation"
          >
            <Menu className="size-4.5" />
          </button>

          {/* The scope switcher appears only when there is a choice to make. A
              single-scope user gets a dropdown with one item and no reason for
              it to exist. */}
          {scopes.length > 1 ? (
            <ScopeSwitcher scopes={scopes} onSwitch={switchScope} />
          ) : scopes.length === 1 ? (
            <span className="hidden text-xs text-muted sm:inline">{scopes[0]?.label}</span>
          ) : null}

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
            className="rounded p-1.5 text-muted transition-colors hover:bg-surface-2 hover:text-body"
            aria-label={mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {mode === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>

          <DropdownMenu
            align="end"
            label="Account"
            items={[
              { label: 'My profile', icon: <User className="size-3.5" />, onSelect: () => undefined },
              {
                label: 'Sign out',
                icon: <LogOut className="size-3.5" />,
                separated: true,
                destructive: true,
                onSelect: () => void signOut(),
              },
            ]}
            trigger={({ ref, onClick, ...aria }) => (
              <button
                ref={ref}
                type="button"
                onClick={onClick}
                {...aria}
                className="flex items-center gap-2 rounded-control px-1.5 py-1 hover:bg-surface-2"
              >
                <Avatar name={user?.full_name} size="sm" />
                <span className="hidden min-w-0 text-left sm:block">
                  <span className="block truncate text-xs font-medium text-body">
                    {user?.full_name}
                  </span>
                  <span className="block truncate text-2xs text-faint">
                    {user?.username ?? user?.email}
                  </span>
                </span>
                <ChevronDown className="size-3 shrink-0 text-faint" aria-hidden />
              </button>
            )}
          />
        </header>

        <ScrollArea className="min-h-0 flex-1" fill>
          <main className="flex flex-1 flex-col px-(--page-gutter) py-4 [--page-gutter:1rem] md:py-5 md:[--page-gutter:1.25rem]">{children}</main>
        </ScrollArea>
      </div>
    </div>
  );
}

function NavLink({
  item,
  route,
  collapsed,
  badge,
  currentPath,
  onNavigate,
}: {
  item: NavItem;
  route: NavRoute;
  collapsed: boolean;
  badge?: number;
  currentPath: string;
  onNavigate: () => void;
}) {
  // Matched by prefix, so a detail route belongs to its list entry — otherwise
  // opening a client deselects Clients in the sidebar and the user appears to
  // have navigated away from where they are.
  const active =
    route.to === '/' ? currentPath === '/' : currentPath.startsWith(route.to);

  return (
    <li>
      <Link
        to={route.to}
        onClick={onNavigate}
        title={collapsed ? item.label : undefined}
        className={cn(
          'flex items-center gap-2.5 rounded-control px-2 py-1.5 text-sm transition-colors',
          active
            ? 'bg-accent-subtle font-medium text-accent'
            : 'text-muted hover:bg-surface-2 hover:text-body',
          collapsed && 'justify-center',
        )}
        aria-current={active ? 'page' : undefined}
      >
        <span className="shrink-0">{route.icon}</span>
        {!collapsed ? <span className="flex-1 truncate">{item.label}</span> : null}
        {/* A zero badge is not rendered: a count of nothing is noise, and a
            permanent "0" trains people to ignore the badge entirely. */}
        {!collapsed && badge ? (
          <Badge tone={active ? 'accent' : 'neutral'}>{badge}</Badge>
        ) : null}
      </Link>
    </li>
  );
}

function ScopeSwitcher({
  scopes,
  onSwitch,
}: {
  scopes: readonly ScopeOption[];
  onSwitch: (option: ScopeOption) => Promise<void>;
}) {
  const active = scopes.find((scope) => scope.active) ?? scopes[0];

  return (
    <DropdownMenu
      align="start"
      label="Change scope"
      items={scopes.map((scope) => ({
        label: (
          <span className="flex items-center gap-2">
            <span className={cn('size-1.5 rounded-full', scope.active ? 'bg-accent' : 'bg-line-strong')} />
            {scope.label}
          </span>
        ),
        onSelect: () => void onSwitch(scope),
        disabled: scope.active,
      }))}
      trigger={({ ref, onClick, ...aria }) => (
        <button
          ref={ref}
          type="button"
          onClick={onClick}
          {...aria}
          className="flex items-center gap-1.5 rounded-control border border-line px-2 py-1 text-xs text-body hover:bg-surface-2"
          // Says what changing it does. A switcher labelled only with a place
          // name looks like a filter, and the consequence — different
          // capabilities, not just fewer rows — is invisible.
          title="Change the scope you are working in. This changes both what you can see and what you can do."
        >
          <span className="max-w-32 truncate">{active?.label ?? 'Scope'}</span>
          <ChevronDown className="size-3 text-faint" aria-hidden />
        </button>
      )}
    />
  );
}
